import { useState } from "react";
import { addIncident } from "../lib/firestore-ops";
import type { IncidentEntry } from "../lib/types";

// CP-log US26: totaal verkocht
const TOTAAL_VERKOCHT = 10000;

// Extend locally to accommodate extra fields written by InstroomWidget
type InstroomEntry = IncidentEntry & { kind?: string; count?: number };

function extractCount(title: string): number {
  const m = /Tussenstand:\s*(\d+)/.exec(title);
  return m ? parseInt(m[1], 10) : 0;
}

function getEntryCount(entry: InstroomEntry): number {
  return entry.count ?? extractCount(entry.title);
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

function BenchmarkContext() {
  return (
    <p className="text-2xs text-zinc-500 mb-4 tabular-nums">
      Vergunning: max 17.500 bezoekers · piekinstroom 15:00–17:00
    </p>
  );
}

export interface InstroomWidgetProps {
  /** Alle incidenten uit de bestaande Firestore-hook — widget filtert zelf op instroom. */
  incidents: IncidentEntry[];
}

export function InstroomWidget({ incidents }: InstroomWidgetProps) {
  const instroom = (incidents as InstroomEntry[])
    .filter(
      (i) => i.kind === "instroom" || i.title.startsWith("Tussenstand:"),
    )
    .sort((a, b) => b.time - a.time);

  const lastCount = instroom.length > 0 ? getEntryCount(instroom[0]) : 0;
  const recent = instroom.slice(0, 5);
  const pct =
    TOTAAL_VERKOCHT > 0
      ? ((lastCount / TOTAAL_VERKOCHT) * 100).toFixed(1)
      : "0.0";
  const barWidth = Math.min(100, (lastCount / TOTAAL_VERKOCHT) * 100);

  const [inputValue, setInputValue] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const parsedInput = Number(inputValue);
  const canSubmit =
    inputValue.trim() !== "" && !isNaN(parsedInput) && parsedInput > 0 && !busy;

  function handleQuickAdd(delta: number): void {
    const base =
      inputValue.trim() !== "" ? Number(inputValue) || lastCount : lastCount;
    setInputValue(String(base + delta));
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    const count = Math.round(Number(inputValue));
    if (isNaN(count) || count <= 0) return;
    setBusy(true);
    const payload: Omit<IncidentEntry, "id"> & { kind: string; count: number } =
      {
        kind: "instroom",
        count,
        title: `Tussenstand: ${count} binnen`,
        severity: "info",
        location: "Hoofdingang",
        time: Date.now(),
        resolved: false,
      };
    void addIncident(payload)
      .then(() => {
        setInputValue("");
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <div
      className="rounded-2xl bg-ink-925 p-4"
      style={{ border: "1px solid rgba(255,255,255,0.06)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          <span className="text-2xs tracking-eyebrow uppercase text-zinc-500">
            Instroom
          </span>
        </div>
        <span className="text-2xs text-zinc-500 tabular">
          {pct}% van {TOTAAL_VERKOCHT.toLocaleString("nl-NL")}
        </span>
      </div>

      {/* Huidig getal groot */}
      <div className="mb-3">
        <div className="text-4xl font-bold tabular text-zinc-100 leading-none">
          {lastCount.toLocaleString("nl-NL")}
        </div>
        <div className="text-2xs text-zinc-500 mt-1">
          {instroom.length === 0 ? "geen stand geregistreerd" : "laatste tussenstand"}
        </div>
      </div>

      {/* Voortgangsbalk */}
      <div className="h-1.5 rounded-full bg-ink-800 mb-4 overflow-hidden">
        <div
          className="h-full rounded-full bg-accent transition-all duration-500"
          style={{ width: `${barWidth}%` }}
        />
      </div>

      {/* 2025 benchmark context */}
      <BenchmarkContext />

      {/* Invoer + snelknoppen */}
      <form onSubmit={handleSubmit}>
        <label htmlFor="instroom-totaal" className="sr-only">
          Nieuw totaal
        </label>
        <input
          id="instroom-totaal"
          type="number"
          min={1}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={`Nieuw totaal (nu: ${lastCount})`}
          className="w-full bg-ink-900 border border-white/[0.08] rounded-lg px-3 py-2 text-sm mb-2 focus:border-accent/60 focus:outline-none tabular"
        />

        {/* Snelknoppen */}
        <div className="flex gap-1.5 mb-3">
          {([100, 250, 500] as const).map((delta) => (
            <button
              key={delta}
              type="button"
              onClick={() => handleQuickAdd(delta)}
              className="flex-1 py-1.5 rounded-lg border border-line text-xs text-zinc-400 hover:text-zinc-200 hover:border-white/20 transition-colors"
            >
              +{delta}
            </button>
          ))}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2 rounded-lg bg-accent text-ink-950 font-semibold text-sm disabled:opacity-40 transition-opacity"
        >
          {busy ? "Bezig..." : "Tussenstand loggen"}
        </button>
      </form>

      {/* Mini-reeks laatste 5 tussenstanden */}
      {recent.length > 0 && (
        <div className="mt-4 border-t border-white/[0.05] pt-3 space-y-1.5">
          <div className="text-2xs text-zinc-600 uppercase tracking-eyebrow mb-1.5">
            Recente tussenstanden
          </div>
          {recent.map((entry, idx) => (
            <div key={entry.id} className="flex items-center justify-between">
              <span className="text-sm tabular text-zinc-300 font-medium">
                {getEntryCount(entry).toLocaleString("nl-NL")}
              </span>
              <div className="flex items-center gap-2 text-2xs text-zinc-500">
                {idx === 0 && <span className="text-accent">huidig</span>}
                <span className="tabular">{fmtTime(entry.time)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
